import "./styles/base.css";
import "./styles/screens.css";
import "./shared/ui/hintTooltip.css";
import "./features/hoverIdle/tutorial/firstAccessTutorialOverlay.css";
import "./features/data/dataPanel.css";
import "./features/payment/paymentPanel.css";
import "./features/settings/settingsPanel.css";
import "./features/settings/settingsAccountSection.css";
import "./features/hoverIdle/hov/hovAmplifier.css";
import "./features/hoverIdle/tim/hovTimBalance.css";
import "./styles/navigation.css";
import "./styles/standby.css";
import "./styles/achievements.css";
import "./styles/records.css";
import "./styles/mpm.css";
import "./styles/game-ui.css";
import "./styles/responsive.css";
import { bootLogNavigationOrigin, bootMark } from "./app/bootTimingLogger";
import { createGameApp, type GameApp } from "./app/createGameApp";
import type { AchievementsState } from "./features/achievements/types";
import type { HovSkillState } from "./features/hoverIdle/hov/hovSkillModel";
import type { TimSkillState } from "./features/hoverIdle/tim/timSkillModel";
import type { HoverIdleModelState } from "./features/hoverIdle/types";
import type { RecordsState } from "./features/records/types";
import { markStartupTrace } from "./app/startupTrace";
import { waitForPaint } from "./shared/ui/waitForPaint";

interface HoverIdleHotData {
  modelState?: HoverIdleModelState;
  hovSkillState?: HovSkillState;
  timSkillState?: TimSkillState;
  achievementsState?: AchievementsState;
  recordsState?: RecordsState;
}

const canvas = document.getElementById("renderCanvas");
const overlay = document.getElementById("overlay");
const bootLoading = document.getElementById("bootLoading");

if (!(canvas instanceof HTMLCanvasElement)) {
  throw new Error("#renderCanvas is missing or not a <canvas>");
}
if (!(overlay instanceof HTMLElement)) {
  throw new Error("#overlay is missing");
}

markStartupTrace("navigation-start", 0);
markStartupTrace("script-boot-start");
bootMark("main.ts module evaluated");
bootLogNavigationOrigin();

const dismissBootLoading = (): void => {
  bootLoading?.remove();
  bootMark("bootLoading removed");
};

let app: GameApp | null = null;
let isDisposed = false;
const hotData = import.meta.hot?.data as HoverIdleHotData | undefined;
const initialModelState = hotData?.modelState ?? null;
const initialHovSkillState = hotData?.hovSkillState ?? null;
const initialTimSkillState = hotData?.timSkillState ?? null;
const initialAchievementsState = hotData?.achievementsState ?? null;
const initialRecordsState = hotData?.recordsState ?? null;

bootMark("createGameApp called");
void createGameApp({
  canvas,
  overlay,
  initialModelState,
  initialHovSkillState,
  initialTimSkillState,
  initialAchievementsState,
  initialRecordsState,
  onStartupGameSurfaceReady: dismissBootLoading,
})
  .then((createdApp) => {
    bootMark("createGameApp resolved");
    if (isDisposed) {
      createdApp.dispose();
      dismissBootLoading();
      return;
    }

    app = createdApp;
    app.start();
    bootMark("renderLoop started");
    markStartupTrace("startup-complete");
    markStartupTrace("interactive");
    dismissBootLoading();
    void waitForPaint().then(() => {
      if (
        performance.getEntriesByName(
          "hover-idle:startup:game-surface-first-paint",
          "mark",
        ).length === 0
      ) {
        markStartupTrace("game-surface-first-paint");
      }
    });
  })
  .catch((error: unknown) => {
    console.error(
      "Failed to start HoverIdle:",
      error instanceof Error ? error.message : String(error),
    );
    dismissBootLoading();
  });

if (import.meta.hot) {
  import.meta.hot.accept();
  import.meta.hot.dispose((data: HoverIdleHotData) => {
    isDisposed = true;
    data.modelState = app?.exportState() ?? initialModelState ?? undefined;
    data.hovSkillState =
      app?.exportHovSkillState() ?? initialHovSkillState ?? undefined;
    data.timSkillState =
      app?.exportTimSkillState() ?? initialTimSkillState ?? undefined;
    data.achievementsState =
      app?.exportAchievementsState() ?? initialAchievementsState ?? undefined;
    data.recordsState =
      app?.exportRecordsState() ?? initialRecordsState ?? undefined;
    app?.dispose();
  });
}
